/**
 * QuestionQualityGate — wizard adım 1'in soru kalite kapısı.
 *
 * Neden var: bir tartışmanın kalitesinin üst sınırı sorunun kalitesidir.
 * Belirsiz bir soru belirsiz bir tartışma değil, HİÇBİR ŞEY hakkında
 * kendinden emin bir tartışma üretir — her uzman soruyu kendi alanına
 * çeker, moderatör bunları sentezler ve ortaya kanıtsız bir terim yığını
 * çıkar (bkz. `question_quality.py` modül docstring'i).
 *
 * Kapı ASLA bloklamaz. Puanlar, gerekçelendirir ve şıklı bir yeniden
 * yazım önerir; "yine de devam et" her zaman bir tık uzaktadır.
 *
 * Akış:
 *   idle → (assessQuestion) → ready
 *        → kullanıcı şıkları işaretler
 *        → (refineQuestionDraft) → proposal
 *        → (applyQualityProposal) → wizard.query güncellenir + yeni rapor
 *
 * KABUK YOK: bu bileşen kendi `Card`'ını çizmez. `QueryComposer`'ın
 * gövdesinin devamı olarak render edilir, çünkü soruyu yazmak ile soruyu
 * netleştirmek TEK bir iştir; iki ayrı kart operatöre iki ayrı görev
 * gibi görünüyordu ve adım 1'i kalabalıklaştırıyordu.
 */
import { useMemo } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CircleCheck,
  Gauge,
  Loader2,
  RefreshCw,
  ShieldQuestion,
  Sparkles,
  Wand2,
} from "lucide-react";

import { useDebateStore } from "../../store/debateStore";
import { isQualityReportFresh, WIZARD_LIMITS } from "../../domain/wizard";
import type {
  QualityQuestion,
  QualityVerdict,
} from "../../transport/questionQualityClient";
import { Button } from "../common/Button";
import { cn } from "../common/cn";
import { t } from "../../i18n";

/**
 * Kart değil, bölüm. Üstteki ince çizgi `QueryComposer`'ın gövdesinden
 * görsel devamlılığı korur — operatör tek bir panelde çalışır.
 */
const SHELL = "flex flex-col gap-4 border-t border-line-soft/50 pt-4";

/* ---------------------------------------------------------------------- *
 * Verdict theming
 * ---------------------------------------------------------------------- */

interface VerdictTheme {
  ring: string;
  text: string;
  border: string;
  bg: string;
  label: string;
}

const VERDICT_THEME: Record<QualityVerdict, VerdictTheme> = {
  ready: {
    ring: "stroke-emerald-400",
    text: "text-emerald-300",
    border: "border-emerald-400/30",
    bg: "bg-emerald-500/10",
    label: t("setup.quality.verdictReady"),
  },
  improvable: {
    ring: "stroke-amber-400",
    text: "text-amber-300",
    border: "border-amber-400/30",
    bg: "bg-amber-500/10",
    label: t("setup.quality.verdictImprovable"),
  },
  vague: {
    ring: "stroke-rose-400",
    text: "text-rose-300",
    border: "border-rose-400/30",
    bg: "bg-rose-500/10",
    label: t("setup.quality.verdictVague"),
  },
};

/* ---------------------------------------------------------------------- *
 * Score ring
 * ---------------------------------------------------------------------- */

function ScoreRing({ score, verdict }: { score: number; verdict: QualityVerdict }) {
  const theme = VERDICT_THEME[verdict];
  const r = 26;
  const circumference = 2 * Math.PI * r;
  const filled = (Math.max(0, Math.min(100, score)) / 100) * circumference;

  return (
    <div
      className="relative grid h-[68px] w-[68px] flex-none place-items-center"
      role="meter"
      aria-valuenow={score}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={t("setup.quality.scoreAria")}
    >
      <svg viewBox="0 0 68 68" className="absolute inset-0 -rotate-90">
        <circle
          cx="34"
          cy="34"
          r={r}
          fill="none"
          strokeWidth="5"
          className="stroke-white/[0.08]"
        />
        <circle
          cx="34"
          cy="34"
          r={r}
          fill="none"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          className={cn(theme.ring, "transition-[stroke-dasharray] duration-500 ease-out")}
        />
      </svg>
      <span className={cn("font-mono text-lg font-semibold tabular-nums", theme.text)}>
        {score}
      </span>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * Dimension bars
 * ---------------------------------------------------------------------- */

function dimensionTone(score: number): string {
  if (score >= 75) return "bg-emerald-400/80";
  if (score >= 50) return "bg-amber-400/80";
  return "bg-rose-400/80";
}

/* ---------------------------------------------------------------------- *
 * One clarifying question — 4 options + free-text escape
 * ---------------------------------------------------------------------- */

interface QuestionCardProps {
  question: QualityQuestion;
  index: number;
  selected: string[];
  other: string;
  disabled: boolean;
  onToggle: (optionId: string) => void;
  onOther: (text: string) => void;
}

function QuestionCard({
  question,
  index,
  selected,
  other,
  disabled,
  onToggle,
  onOther,
}: QuestionCardProps) {
  const answered = selected.length > 0 || other.trim().length > 0;

  return (
    <li
      className={cn(
        "rounded-xl border p-3.5 transition-colors sm:p-4",
        answered
          ? "border-[hsl(var(--accent)/0.45)] bg-[hsl(var(--accent)/0.06)]"
          : "border-white/[0.07] bg-surface-0/40",
      )}
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className={cn(
            "mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full text-[10px] font-semibold",
            answered
              ? "bg-[hsl(var(--accent)/0.85)] text-white"
              : "border border-white/15 bg-surface-2/60 text-fg-subtle",
          )}
        >
          {answered ? <Check className="h-3 w-3" strokeWidth={3} /> : index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug text-fg-strong">
            {question.question}
          </p>
          {question.why ? (
            <p className="mt-1 text-[11px] leading-relaxed text-fg-subtle">
              {question.why}
            </p>
          ) : null}
        </div>
      </div>

      <div
        role={question.allowMultiple ? "group" : "radiogroup"}
        aria-label={question.question}
        className="mt-3 grid gap-1.5 sm:grid-cols-2"
      >
        {question.options.map((opt) => {
          const isOn = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              role={question.allowMultiple ? "checkbox" : "radio"}
              aria-checked={isOn}
              disabled={disabled}
              onClick={() => onToggle(opt.id)}
              title={opt.detail || opt.label}
              className={cn(
                "focus-ring flex items-start gap-2 rounded-lg border px-2.5 py-2 text-left transition",
                "disabled:cursor-not-allowed disabled:opacity-55",
                isOn
                  ? "border-[hsl(var(--accent)/0.7)] bg-[hsl(var(--accent)/0.16)] text-fg-strong"
                  : "border-white/[0.08] bg-surface-2/40 text-fg-muted hover:border-[hsl(var(--accent)/0.4)] hover:text-fg-strong",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "mt-[3px] h-3 w-3 flex-none border transition",
                  question.allowMultiple ? "rounded-[4px]" : "rounded-full",
                  isOn
                    ? "border-transparent bg-[hsl(var(--accent))]"
                    : "border-white/25 bg-transparent",
                )}
              />
              <span className="min-w-0">
                <span className="block text-xs font-medium leading-snug">{opt.label}</span>
                {opt.detail ? (
                  <span className="mt-0.5 block text-[10.5px] leading-snug text-fg-subtle">
                    {opt.detail}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {question.allowOther ? (
        <input
          type="text"
          value={other}
          disabled={disabled}
          onChange={(e) => onOther(e.target.value)}
          placeholder={t("setup.quality.otherPlaceholder")}
          maxLength={400}
          aria-label={t("setup.quality.otherAria")}
          className={cn(
            "focus-ring mt-2 w-full rounded-lg border border-white/10 bg-surface-0/60",
            "px-3 py-1.5 text-xs text-fg-strong placeholder:text-fg-subtle",
            "disabled:cursor-not-allowed disabled:opacity-55",
          )}
        />
      ) : null}
    </li>
  );
}

/* ---------------------------------------------------------------------- *
 * Gate
 * ---------------------------------------------------------------------- */

export function QuestionQualityGate() {
  const query = useDebateStore((s) => s.wizard.query);
  const quality = useDebateStore((s) => s.wizard.quality);
  const assessQuestion = useDebateStore((s) => s.assessQuestion);
  const toggleQualityOption = useDebateStore((s) => s.toggleQualityOption);
  const setQualityOther = useDebateStore((s) => s.setQualityOther);
  const refineQuestionDraft = useDebateStore((s) => s.refineQuestionDraft);
  const applyQualityProposal = useDebateStore((s) => s.applyQualityProposal);
  const discardQualityProposal = useDebateStore((s) => s.discardQualityProposal);
  const dismissQualityGate = useDebateStore((s) => s.dismissQualityGate);

  const trimmed = query.trim();
  const tooShort = trimmed.length < WIZARD_LIMITS.queryMin;
  const fresh = isQualityReportFresh(quality, query);
  const report = fresh ? quality.report : null;
  const busy = quality.status === "assessing" || quality.status === "refining";

  const answeredCount = useMemo(() => {
    if (!report) return 0;
    return report.questions.filter((q) => {
      const draft = quality.answers[q.id];
      return Boolean(draft && (draft.selected.length > 0 || draft.other.trim().length > 0));
    }).length;
  }, [report, quality.answers]);

  /* -- Idle: invite the operator to run the gate --------------------- */
  if (!report && quality.status !== "assessing") {
    return (
      <section className={SHELL}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span
              aria-hidden
              className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-accent-400/30 bg-accent-500/10 text-accent-200"
            >
              <Gauge className="h-4 w-4" strokeWidth={1.5} />
            </span>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-fg-strong">
                {t("setup.quality.title")}
              </h4>
              <p className="mt-0.5 text-[11px] leading-relaxed text-fg-subtle">
                {t("setup.quality.idleHint")}
              </p>
              {quality.status === "error" && quality.error ? (
                <p className="mt-1.5 text-[11px] text-rose-300">{quality.error}</p>
              ) : null}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={tooShort}
            onClick={() => void assessQuestion({ force: true })}
            leftIcon={<Sparkles className="h-3.5 w-3.5" />}
          >
            {quality.status === "error"
              ? t("setup.quality.retry")
              : t("setup.quality.assess")}
          </Button>
        </div>
      </section>
    );
  }

  /* -- Assessing ------------------------------------------------------ */
  if (!report) {
    return (
      <section className={cn(SHELL, "flex items-center gap-3")}>
        <Loader2 className="h-4 w-4 animate-spin text-accent-300" aria-hidden />
        <div>
          <p className="text-sm font-medium text-fg-strong">
            {t("setup.quality.assessing")}
          </p>
          <p className="text-[11px] text-fg-subtle">
            {t("setup.quality.assessingHint")}
          </p>
        </div>
      </section>
    );
  }

  const theme = VERDICT_THEME[report.verdict];
  const proposal = quality.proposal;
  const showQuestions =
    report.verdict !== "ready" && !quality.dismissed && report.questions.length > 0;

  return (
    <section className={SHELL}>
      <header className="flex flex-wrap items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            "grid h-7 w-7 flex-none place-items-center rounded-lg border",
            theme.border,
            theme.bg,
            theme.text,
          )}
        >
          {report.verdict === "ready" ? (
            <CircleCheck className="h-4 w-4" strokeWidth={1.6} />
          ) : (
            <ShieldQuestion className="h-4 w-4" strokeWidth={1.6} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-fg-strong">
            {t("setup.quality.title")}
          </h4>
          <p className="text-[11px] leading-relaxed text-fg-subtle">
            {t("setup.quality.method")}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => void assessQuestion({ force: true })}
          leftIcon={<RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} />}
        >
          {t("setup.quality.reassess")}
        </Button>
      </header>

      <div className="flex flex-col gap-4">
        {/* Score + verdict + summary */}
        <div className="flex items-start gap-4">
          <ScoreRing score={report.score} verdict={report.verdict} />
          <div className="min-w-0 flex-1">
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                theme.border,
                theme.bg,
                theme.text,
              )}
            >
              {theme.label}
            </span>
            <p className="mt-1.5 text-xs leading-relaxed text-fg-muted">{report.summary}</p>
          </div>
        </div>

        {/* Rubric */}
        <ul className="grid gap-1.5">
          {report.dimensions.map((d) => (
            <li key={d.id} className="flex items-center gap-2.5" title={d.hint}>
              <span className="w-[104px] flex-none truncate text-[11px] text-fg-subtle">
                {d.label}
              </span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
                <span
                  className={cn("block h-full rounded-full transition-[width] duration-500", dimensionTone(d.score))}
                  style={{ width: `${Math.max(2, d.score)}%` }}
                />
              </span>
              <span className="w-7 flex-none text-right font-mono text-[10px] tabular-nums text-fg-subtle">
                {d.score}
              </span>
            </li>
          ))}
        </ul>

        {/* The honest, non-LLM part: what the real intake pipeline extracts. */}
        <div className="flex flex-wrap items-center gap-1.5 border-t border-white/[0.05] pt-3">
          <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
            {t("setup.quality.pipelineTitle")}
          </span>
          <ProbeChip
            label={t("setup.quality.probeRequirements")}
            value={String(report.pipeline.requirementCount)}
            bad={report.pipeline.tautologicalRequirement}
          />
          <ProbeChip
            label={t("setup.quality.probeEntities")}
            value={String(report.pipeline.entityCount)}
            bad={report.pipeline.entityCount === 0}
          />
          <ProbeChip
            label={t("setup.quality.probeConstraints")}
            value={String(report.pipeline.constraintKeys.length)}
            bad={report.pipeline.constraintKeys.length === 0}
          />
          <ProbeChip
            label={t("setup.quality.probeAgreement")}
            value={`%${Math.round(report.agreement * 100)}`}
            bad={report.agreement < 0.6}
          />
        </div>

        {report.missing.length > 0 && report.verdict !== "ready" ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-400/20 bg-amber-500/[0.07] px-3 py-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-300" aria-hidden />
            <p className="text-[11px] leading-relaxed text-amber-100/90">
              <span className="font-medium">{t("setup.quality.missingTitle")}</span>{" "}
              {report.missing.join(" · ")}
            </p>
          </div>
        ) : null}

        {/* Guided refinement */}
        {showQuestions ? (
          <div className="flex flex-col gap-3 border-t border-white/[0.05] pt-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-fg-strong">
                {t("setup.quality.questionsTitle")}
              </p>
              <span className="font-mono text-[10px] tabular-nums text-fg-subtle">
                {answeredCount}/{report.questions.length}
              </span>
            </div>

            <ul className="flex flex-col gap-2.5">
              {report.questions.map((q, i) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  index={i}
                  selected={quality.answers[q.id]?.selected ?? []}
                  other={quality.answers[q.id]?.other ?? ""}
                  disabled={busy}
                  onToggle={(optionId) =>
                    toggleQualityOption(q.id, optionId, q.allowMultiple)
                  }
                  onOther={(text) => setQualityOther(q.id, text)}
                />
              ))}
            </ul>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button variant="ghost" size="sm" disabled={busy} onClick={dismissQualityGate}>
                {t("setup.quality.skip")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={quality.status === "refining"}
                disabled={busy || answeredCount === 0}
                onClick={() => void refineQuestionDraft()}
                leftIcon={
                  quality.status === "refining" ? undefined : <Wand2 className="h-3.5 w-3.5" />
                }
              >
                {quality.status === "refining"
                  ? t("setup.quality.rewriting")
                  : t("setup.quality.rewrite")}
              </Button>
            </div>
          </div>
        ) : null}

        {quality.status === "error" && quality.error ? (
          <p className="text-[11px] text-rose-300">{quality.error}</p>
        ) : null}

        {/* Proposal — before/after with the re-scored result */}
        {proposal ? (
          <div className="flex flex-col gap-3 rounded-xl border border-[hsl(var(--accent)/0.35)] bg-[hsl(var(--accent)/0.07)] p-3.5">
            <div className="flex items-center gap-2">
              <Wand2 className="h-3.5 w-3.5 text-accent-200" aria-hidden />
              <p className="text-xs font-semibold text-fg-strong">
                {t("setup.quality.proposalTitle")}
              </p>
              <span className="ml-auto flex items-center gap-1.5 font-mono text-[11px] tabular-nums">
                <span className="text-fg-subtle">{report.score}</span>
                <ArrowRight className="h-3 w-3 text-fg-subtle" aria-hidden />
                <span className={VERDICT_THEME[proposal.report.verdict].text}>
                  {proposal.report.score}
                </span>
              </span>
            </div>

            <p className="rounded-lg border border-white/[0.07] bg-surface-0/60 px-3 py-2 text-xs leading-relaxed text-fg-strong">
              {proposal.refined}
            </p>

            {proposal.changes.length > 0 ? (
              <ul className="flex flex-col gap-0.5">
                {proposal.changes.map((c, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-fg-muted">
                    <Check className="mt-0.5 h-3 w-3 flex-none text-emerald-400" aria-hidden />
                    {c}
                  </li>
                ))}
              </ul>
            ) : null}

            {proposal.assumptions.length > 0 ? (
              <p className="text-[10.5px] leading-relaxed text-fg-subtle">
                <span className="font-medium text-fg-muted">
                  {t("setup.quality.assumptionsTitle")}
                </span>{" "}
                {proposal.assumptions.join(" · ")}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={discardQualityProposal}>
                {t("setup.quality.proposalDiscard")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={applyQualityProposal}
                leftIcon={<Check className="h-3.5 w-3.5" />}
              >
                {t("setup.quality.proposalApply")}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ProbeChip({
  label,
  value,
  bad,
}: {
  label: string;
  value: string;
  bad: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] tabular-nums",
        bad
          ? "border-rose-400/25 bg-rose-500/10 text-rose-200"
          : "border-white/[0.08] bg-surface-2/50 text-fg-muted",
      )}
    >
      <span className="font-sans text-[9.5px] uppercase tracking-wide opacity-70">{label}</span>
      {value}
    </span>
  );
}

export default QuestionQualityGate;
